import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Checkbox, InputAdornment, TextField, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import Check from '@mui/icons-material/Check';
import FilterList from '@mui/icons-material/FilterList';
import Search from '@mui/icons-material/Search';
import { monoSx } from '../dashboard/styles';
import SelectedPills from './SelectedPills';

export interface SelectableItem {
  id: string;
  primary: string;
  /** Shown under `primary` in monospace; also searchable. */
  secondary?: string;
}

interface SelectableListProps {
  items: SelectableItem[];
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
  /** Plural noun for placeholders, e.g. "fulfillment contexts". */
  itemNoun?: string;
  maxVisiblePills?: number;
  height?: number;
  rowHeight?: number;
}

const OVERSCAN = 6;

/**
 * Controlled multi-select list: search, "selected only" filter, removable pills, select-all over the visible rows,
 * and a windowed list so thousands of rows stay responsive.
 */
const SelectableList: React.FC<SelectableListProps> = ({
  items,
  selectedIds,
  onChange,
  itemNoun = 'items',
  maxVisiblePills = 8,
  height = 360,
  rowHeight = 52,
}) => {
  const [query, setQuery] = useState('');
  const [selectedOnly, setSelectedOnly] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);
  const itemsById = useMemo(() => new Map(items.map((item) => [item.id, item])), [items]);

  const visibleItems = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return items.filter(
      (item) =>
        (!selectedOnly || selected.has(item.id)) &&
        (!needle || item.primary.toLowerCase().includes(needle) || item.id.toLowerCase().includes(needle) || item.secondary?.toLowerCase().includes(needle)),
    );
  }, [items, query, selectedOnly, selected]);

  // A new filter starts at the top of the list.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    setScrollTop(0);
  }, [query, selectedOnly]);

  const visibleSelectedCount = visibleItems.reduce((count, item) => count + (selected.has(item.id) ? 1 : 0), 0);
  const allVisibleSelected = visibleItems.length > 0 && visibleSelectedCount === visibleItems.length;

  const toggle = (id: string) => onChange(selected.has(id) ? selectedIds.filter((s) => s !== id) : [...selectedIds, id]);

  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      const visibleIds = new Set(visibleItems.map((item) => item.id));
      onChange(selectedIds.filter((id) => !visibleIds.has(id)));
    } else {
      onChange([...selectedIds, ...visibleItems.filter((item) => !selected.has(item.id)).map((item) => item.id)]);
    }
  };

  const start = Math.max(0, Math.floor(scrollTop / rowHeight) - OVERSCAN);
  const end = Math.min(visibleItems.length, Math.ceil((scrollTop + height) / rowHeight) + OVERSCAN);

  const pills = selectedIds.map((id) => ({ id, label: itemsById.get(id)?.primary ?? id }));

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 3, overflow: 'hidden', bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.25, px: 1.5, py: 1.25, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          size="small"
          sx={{ flex: '1 1 240px' }}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search ${items.length} ${itemNoun}`}
          inputProps={{ 'aria-label': `Search ${itemNoun}` }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
            sx: { height: 36, borderRadius: 4.5, fontSize: 13, bgcolor: 'background.paper' },
          }}
        />
        <Button
          variant="outlined"
          size="small"
          aria-pressed={selectedOnly}
          onClick={() => setSelectedOnly((v) => !v)}
          startIcon={selectedOnly ? <Check sx={{ fontSize: 16 }} /> : <FilterList sx={{ fontSize: 16 }} />}
          sx={{
            height: 32,
            px: 1.5,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            fontSize: 13,
            fontWeight: 500,
            borderRadius: 2,
            color: selectedOnly ? 'brand.onContainer' : 'text.secondary',
            borderColor: selectedOnly ? 'transparent' : 'divider',
            bgcolor: selectedOnly ? 'brand.container' : 'background.paper',
            '&:hover': { borderColor: selectedOnly ? 'transparent' : 'text.secondary', bgcolor: selectedOnly ? 'brand.container' : 'background.paper' },
          }}
        >
          Selected only · {selectedIds.length}
        </Button>
      </Box>

      <SelectedPills items={pills} maxVisible={maxVisiblePills} onRemove={toggle} onClear={() => onChange([])} />

      <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, height: 40, borderBottom: 1, borderColor: 'divider', cursor: 'pointer' }}>
        <Checkbox
          size="small"
          checked={allVisibleSelected}
          indeterminate={visibleSelectedCount > 0 && !allVisibleSelected}
          disabled={!visibleItems.length}
          onChange={toggleAllVisible}
          sx={{ '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: 'primary.dark' } }}
        />
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
          Select all {visibleItems.length}
        </Typography>
      </Box>

      <Box ref={scrollRef} onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)} sx={{ height, overflowY: 'auto', position: 'relative' }}>
        {visibleItems.length === 0 ? (
          <Typography sx={{ p: 3, textAlign: 'center', fontSize: 13, color: 'text.secondary' }}>
            {selectedOnly && !selectedIds.length ? `No ${itemNoun} selected` : `No ${itemNoun} match your search`}
          </Typography>
        ) : (
          <Box role="list" sx={{ height: visibleItems.length * rowHeight, position: 'relative' }}>
            {visibleItems.slice(start, end).map((item, offset) => {
              const isSelected = selected.has(item.id);
              return (
                <Box
                  key={item.id}
                  role="listitem"
                  component="label"
                  data-selected={isSelected}
                  sx={(theme) => ({
                    position: 'absolute',
                    top: (start + offset) * rowHeight,
                    left: 0,
                    right: 0,
                    height: rowHeight,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1,
                    cursor: 'pointer',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
                    '&:hover': { bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.1) : theme.palette.action.hover },
                  })}
                >
                  <Checkbox
                    size="small"
                    checked={isSelected}
                    onChange={() => toggle(item.id)}
                    inputProps={{ 'aria-label': item.primary }}
                    sx={{ '&.Mui-checked': { color: 'primary.dark' } }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: 14, fontWeight: 500, color: 'text.primary' }} title={item.primary}>
                      {item.primary}
                    </Typography>
                    {item.secondary && (
                      <Typography noWrap component="div" sx={{ ...monoSx, fontSize: 11, color: 'text.secondary' }} title={item.secondary}>
                        {item.secondary}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SelectableList;
